import { Skeleton, Box, Typography } from "@mui/material";
import { getNewsById } from "../services";
import { useWpFetch } from "../hooks/useWpFetch";
import { useParams } from "react-router";
import dayjs from "dayjs";

export const SingleNews = () => {
  const { id } = useParams<{ id: string }>();
  const { data: details, loading } = useWpFetch(
    () => (id ? getNewsById(id) : null),
    [id],
    {} as {
      title?: string;
      content?: string;
      date?: string;
      featured_media?: string;
      gallery?: { large: string; thumb: string }[];
    },
  );

  if (loading) {
    return (
      <Box className="article-main">
        <Skeleton variant="text" width="40%" height={48} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="20%" height={20} sx={{ mb: 2 }} />
        <Skeleton
          variant="rectangular"
          height={300}
          sx={{ borderRadius: 2, mb: 2 }}
        />
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="60%" />
      </Box>
    );
  }

  const imageUrl = details.featured_media || "";
  const gallery = details.gallery || [];
  const hasContent = details.content?.replace(/<[^>]*>/g, "").trim();

  return (
    <Box className="article-main">
      {/* Title */}
      <Typography
        variant="h4"
        sx={{
          fontFamily: '"Cinzel", serif',
          color: "#4a3728",
          borderBottom: "2px solid #d4b483",
          pb: 1,
          mb: 1,
        }}
      >
        {details.title}
      </Typography>

      {/* Date */}
      {details.date && (
        <Typography
          variant="caption"
          sx={{
            color: "#8a7a68",
            mb: 3,
            display: "block",
            fontFamily: '"EB Garamond", serif',
            fontSize: "0.9rem",
          }}
        >
          {dayjs(details.date).format("MMMM D, YYYY")}
        </Typography>
      )}

      {/* Featured image */}
      {imageUrl && (
        <Box
          component="img"
          src={imageUrl}
          alt={details.title}
          sx={{
            width: "100%",
            maxHeight: 450,
            objectFit: "cover",
            borderRadius: 2,
            border: "1px solid #d4c4a8",
            mb: 3,
          }}
        />
      )}

      {/* Content */}
      {hasContent && (
        <Box
          sx={{
            color: "#4a3728",
            lineHeight: 1.8,
            mb: 3,
            "& img": { maxWidth: "100%", borderRadius: 1 },
            "& p": { mb: 1.5 },
          }}
          dangerouslySetInnerHTML={{ __html: details.content || "" }}
        />
      )}

      {/* Gallery */}
      {gallery.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              fontFamily: '"Cinzel", serif',
              color: "#4a3728",
              mb: 1,
            }}
          >
            Gallery
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "1fr 1fr 1fr",
              },
              gap: 2,
            }}
          >
            {gallery.map((item: { large: string; thumb: string }) => (
              <Box
                key={item.large}
                component="img"
                src={item.large}
                alt=""
                sx={{
                  width: "100%",
                  height: 220,
                  objectFit: "cover",
                  borderRadius: 2,
                  border: "1px solid #d4c4a8",
                  cursor: "pointer",
                }}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};
